"use client";

import { useLanguage } from "@/app/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const content = {
  en: {
    badge: "Legal & Privacy",
    title: "Privacy Policy",
    subtitle:
      "Your trust is sacred to us. This policy explains how the 18m Padi Karuppanasamy Temple website handles your information.",
    lastUpdated: "Last Updated: June 2026",
    intro: {
      title: "Our Commitment to Your Privacy",
      body: "The 18m Padi Karuppanasamy Temple website is operated by Trustee Kalimuthu and family on behalf of the temple at Sukkampatti, Kadavoor Thaluk, Karur District, Tamil Nadu. This website is a devotional and informational platform maintained to serve the devotee community. We are fully committed to protecting any information you share with us.",
    },
    sections: [
      {
        icon: "📋",
        title: "1. Information We Collect",
        items: [
          {
            subtitle: "Contact Form Submissions",
            body: "When you use the contact form on our website, we collect your name, phone number, and the message you submit. This information is used solely to respond to your enquiry about the temple, pooja schedules, events, or donations.",
          },
          {
            subtitle: "No Automatic Tracking",
            body: "This website does not use third-party analytics tools, advertising trackers, or cookies that collect personal data without your knowledge. We do not track your browsing behaviour across other websites.",
          },
          {
            subtitle: "WhatsApp Interactions",
            body: "If you contact us via WhatsApp using the link on our website, your communication is handled through WhatsApp's own platform and is subject to WhatsApp's privacy policy. We do not store WhatsApp message data independently.",
          },
        ],
      },
      {
        icon: "🔒",
        title: "2. How We Use Your Information",
        items: [
          {
            subtitle: "Responding to Enquiries",
            body: "Contact form details (name, phone, message) are used exclusively to respond to your enquiry. We do not use this information for marketing, advertising, or any commercial purpose.",
          },
          {
            subtitle: "Temple Operations",
            body: "Information provided for donation arrangements, special pooja bookings, or event participation is used only for that specific purpose and is handled with care by the temple trustee.",
          },
          {
            subtitle: "No Data Sale",
            body: "We do not sell, rent, trade, or share your personal information with any third party, commercial entity, or advertiser. Your details remain with the temple administration only.",
          },
        ],
      },
      {
        icon: "🗄️",
        title: "3. Data Storage & Security",
        items: [
          {
            subtitle: "Storage",
            body: "Contact form submissions may be stored temporarily to enable the temple trustee to respond. No large-scale database of devotee information is maintained by this website.",
          },
          {
            subtitle: "Security",
            body: "We take reasonable measures to protect your information from unauthorised access. However, as a small temple website, we encourage you not to submit sensitive personal or financial information through the contact form.",
          },
          {
            subtitle: "Retention",
            body: "Contact and enquiry data is retained only as long as needed to respond to your request. We do not build long-term personal profiles of website visitors.",
          },
        ],
      },
      {
        icon: "🌐",
        title: "4. Third-Party Links",
        items: [
          {
            subtitle: "Google Maps",
            body: "Our website embeds Google Maps to help devotees locate the temple. Google Maps is subject to Google's own privacy policy. We do not control the data Google collects through this embed.",
          },
          {
            subtitle: "WhatsApp",
            body: "The WhatsApp button on our site opens a WhatsApp chat. WhatsApp is subject to Meta's privacy policy. We are not responsible for how WhatsApp handles your data.",
          },
          {
            subtitle: "Phone Calls",
            body: "Clicking a phone number on our website initiates a call through your device. Call data is handled by your mobile carrier and is not collected by us.",
          },
        ],
      },
      {
        icon: "👶",
        title: "5. Children's Privacy",
        items: [
          {
            subtitle: "No Targeted Collection",
            body: "This website is a devotional platform open to all ages. We do not knowingly collect personal information from children under the age of 13. If you are a parent or guardian and believe a child has submitted information to us, please contact us and we will delete it promptly.",
          },
        ],
      },
      {
        icon: "✏️",
        title: "6. Your Rights",
        items: [
          {
            subtitle: "Access & Deletion",
            body: "You have the right to request access to any personal information we hold about you, and to request its deletion. To do so, please contact us via phone or through the contact form on our website.",
          },
          {
            subtitle: "Correction",
            body: "If you believe information we hold about you is inaccurate, please let us know and we will correct it without delay.",
          },
        ],
      },
      {
        icon: "🔄",
        title: "7. Changes to This Policy",
        items: [
          {
            subtitle: "Updates",
            body: "We may update this Privacy Policy from time to time to reflect changes in our website or legal requirements. The 'Last Updated' date at the top of this page will always reflect the most recent revision. Continued use of the website after changes are posted constitutes your acceptance of the updated policy.",
          },
        ],
      },
      {
        icon: "📞",
        title: "8. Contact Us",
        items: [
          {
            subtitle: "Trustee: Kalimuthu",
            body: "For any privacy-related questions, requests, or concerns regarding this website, please contact the temple administration directly.",
          },
        ],
      },
    ],
    contact: {
      label: "Temple Contact",
      address: "Kadavoor Thaluk, Kaniyalampatti Road, Sukkampatti, Karur, Tamil Nadu",
      phones: ["9597255401", "9361986095"],
      hours: "Open All 7 Days · Morning 4 AM – 12 PM · Evening 4 PM – 7 PM",
    },
    disclaimer: {
      title: "Disclaimer",
      body: "This website is maintained for devotional and informational purposes only. All content related to temple timings, pooja schedules, festivals, and deity information is provided in good faith. The temple administration is not liable for any inaccuracies arising from changes not yet reflected on the website. For the most current information, please call the temple directly.",
    },
  },

  ta: {
    badge: "சட்ட & தனியுரிமை",
    title: "தனியுரிமைக் கொள்கை",
    subtitle:
      "உங்கள் நம்பிக்கை எங்களுக்கு புனிதமானது. இந்தக் கொள்கை 18ம் படி கருப்பண்ணசாமி கோவில் இணையதளம் உங்கள் தகவல்களை எவ்வாறு கையாளுகிறது என்பதை விளக்குகிறது.",
    lastUpdated: "கடைசியாக புதுப்பிக்கப்பட்டது: ஜூன் 2026",
    intro: {
      title: "உங்கள் தனியுரிமையில் எங்கள் உறுதிமொழி",
      body: "18ம் படி கருப்பண்ணசாமி கோவில் இணையதளம் கோவிலின் சார்பாக நம்பகர் களிமுத்து மற்றும் குடும்பத்தினரால் இயக்கப்படுகிறது. இந்த இணையதளம் பக்தர் சமுதாயத்திற்கு சேவை செய்ய பராமரிக்கப்படும் பக்தி மற்றும் தகவல் தளமாகும். நீங்கள் பகிரும் எந்த தகவலையும் பாதுகாக்க நாங்கள் முழுமையாக அர்ப்பணிக்கப்பட்டுள்ளோம்.",
    },
    sections: [
      {
        icon: "📋",
        title: "1. நாங்கள் சேகரிக்கும் தகவல்கள்",
        items: [
          {
            subtitle: "தொடர்பு படிவ சமர்ப்பிப்புகள்",
            body: "எங்கள் இணையதளத்தில் தொடர்பு படிவத்தை நீங்கள் பயன்படுத்தும்போது, உங்கள் பெயர், தொலைபேசி எண் மற்றும் சமர்ப்பிக்கும் செய்தியை சேகரிக்கிறோம். இந்த தகவல் கோவில், பூஜை நேரங்கள், நிகழ்வுகள் அல்லது நன்கொடைகள் பற்றிய உங்கள் விசாரணைக்கு பதிலளிக்க மட்டுமே பயன்படுத்தப்படுகிறது.",
          },
          {
            subtitle: "தானியங்கி கண்காணிப்பு இல்லை",
            body: "இந்த இணையதளம் மூன்றாம் தரப்பு பகுப்பாய்வு கருவிகள், விளம்பர கண்காணிப்பாளர்கள் அல்லது உங்கள் அறிவு இல்லாமல் தனிப்பட்ட தரவை சேகரிக்கும் குக்கீகளை பயன்படுத்துவதில்லை.",
          },
          {
            subtitle: "வாட்ஸ்அப் தொடர்புகள்",
            body: "எங்கள் இணையதளத்தில் உள்ள இணைப்பு வழியாக வாட்ஸ்அப் மூலம் எங்களை தொடர்பு கொண்டால், உங்கள் தொடர்பு வாட்ஸ்அப்பின் சொந்த தளத்தில் கையாளப்படுகிறது.",
          },
        ],
      },
      {
        icon: "🔒",
        title: "2. உங்கள் தகவல்களை நாங்கள் எவ்வாறு பயன்படுத்துகிறோம்",
        items: [
          {
            subtitle: "விசாரணைகளுக்கு பதிலளித்தல்",
            body: "தொடர்பு படிவ விவரங்கள் (பெயர், தொலைபேசி, செய்தி) உங்கள் விசாரணைக்கு பதிலளிக்க மட்டுமே பயன்படுத்தப்படுகின்றன. சந்தைப்படுத்தல் அல்லது வணிக நோக்கத்திற்காக இந்த தகவல்களை நாங்கள் பயன்படுத்துவதில்லை.",
          },
          {
            subtitle: "கோவில் செயல்பாடுகள்",
            body: "நன்கொடை ஏற்பாடுகள், சிறப்பு பூஜை முன்பதிவுகள் அல்லது நிகழ்வு பங்கேற்புக்காக வழங்கப்பட்ட தகவல்கள் அந்த குறிப்பிட்ட நோக்கத்திற்கு மட்டுமே பயன்படுத்தப்படுகின்றன.",
          },
          {
            subtitle: "தரவு விற்பனை இல்லை",
            body: "உங்கள் தனிப்பட்ட தகவல்களை நாங்கள் எந்த மூன்றாம் தரப்பிற்கும் விற்கவோ, வாடகைக்கு விடவோ, வர்த்தகம் செய்யவோ அல்லது பகிரவோ மாட்டோம்.",
          },
        ],
      },
      {
        icon: "🗄️",
        title: "3. தரவு சேமிப்பு & பாதுகாப்பு",
        items: [
          {
            subtitle: "சேமிப்பு",
            body: "கோவில் நம்பகர் பதிலளிக்க உதவுவதற்காக தொடர்பு படிவ சமர்ப்பிப்புகள் தற்காலிகமாக சேமிக்கப்படலாம். இந்த இணையதளத்தால் பக்தர் தகவல்களின் பெரிய அளவிலான தரவுத்தளம் பராமரிக்கப்படுவதில்லை.",
          },
          {
            subtitle: "பாதுகாப்பு",
            body: "அங்கீகரிக்கப்படாத அணுகலிலிருந்து உங்கள் தகவல்களை பாதுகாக்க நாங்கள் நியாயமான நடவடிக்கைகள் எடுக்கிறோம். தொடர்பு படிவம் மூலம் முக்கியமான தனிப்பட்ட அல்லது நிதி தகவல்களை சமர்ப்பிக்காமல் இருக்குமாறு நாங்கள் ஊக்குவிக்கிறோம்.",
          },
          {
            subtitle: "தக்கவைப்பு",
            body: "தொடர்பு மற்றும் விசாரணை தரவு உங்கள் கோரிக்கைக்கு பதிலளிக்க தேவையான காலம் மட்டுமே வைக்கப்படுகிறது.",
          },
        ],
      },
      {
        icon: "🌐",
        title: "4. மூன்றாம் தரப்பு இணைப்புகள்",
        items: [
          {
            subtitle: "கூகிள் மேப்ஸ்",
            body: "பக்தர்கள் கோவிலை கண்டுபிடிக்க உதவ எங்கள் இணையதளம் கூகிள் மேப்ஸை உட்பொதிக்கிறது. கூகிள் மேப்ஸ் கூகிளின் சொந்த தனியுரிமைக் கொள்கைக்கு உட்பட்டது.",
          },
          {
            subtitle: "வாட்ஸ்அப்",
            body: "எங்கள் தளத்தில் உள்ள வாட்ஸ்அப் பொத்தான் வாட்ஸ்அப் அரட்டையை திறக்கிறது. வாட்ஸ்அப் மெட்டாவின் தனியுரிமைக் கொள்கைக்கு உட்பட்டது.",
          },
          {
            subtitle: "தொலைபேசி அழைப்புகள்",
            body: "எங்கள் இணையதளத்தில் தொலைபேசி எண்ணை கிளிக் செய்வது உங்கள் சாதனம் மூலம் அழைப்பை தொடங்குகிறது. அழைப்பு தரவு உங்கள் மொபைல் சேவை வழங்குனரால் கையாளப்படுகிறது.",
          },
        ],
      },
      {
        icon: "👶",
        title: "5. குழந்தைகளின் தனியுரிமை",
        items: [
          {
            subtitle: "இலக்கு சேகரிப்பு இல்லை",
            body: "இந்த இணையதளம் அனைத்து வயதினருக்கும் திறந்த பக்தி தளமாகும். 13 வயதிற்கு கீழ் உள்ள குழந்தைகளிடமிருந்து நாங்கள் தெரிந்தே தனிப்பட்ட தகவல்களை சேகரிப்பதில்லை.",
          },
        ],
      },
      {
        icon: "✏️",
        title: "6. உங்கள் உரிமைகள்",
        items: [
          {
            subtitle: "அணுகல் & நீக்கம்",
            body: "நாங்கள் உங்களைப் பற்றி வைத்திருக்கும் எந்த தனிப்பட்ட தகவலையும் அணுக கோரவும், அதை நீக்க கோரவும் உங்களுக்கு உரிமை உண்டு.",
          },
          {
            subtitle: "திருத்தம்",
            body: "நாங்கள் உங்களைப் பற்றி வைத்திருக்கும் தகவல் துல்லியமற்றது என்று நீங்கள் நம்பினால், எங்களுக்கு தெரிவியுங்கள், தாமதமின்றி அதை சரிசெய்வோம்.",
          },
        ],
      },
      {
        icon: "🔄",
        title: "7. இந்தக் கொள்கையில் மாற்றங்கள்",
        items: [
          {
            subtitle: "புதுப்பிப்புகள்",
            body: "எங்கள் இணையதளம் அல்லது சட்ட தேவைகளில் ஏற்படும் மாற்றங்களை பிரதிபலிக்க இந்த தனியுரிமைக் கொள்கையை அவ்வப்போது புதுப்பிக்கலாம். இந்த பக்கத்தின் மேலே உள்ள 'கடைசியாக புதுப்பிக்கப்பட்டது' தேதி எப்போதும் சமீபத்திய திருத்தத்தை பிரதிபலிக்கும்.",
          },
        ],
      },
      {
        icon: "📞",
        title: "8. எங்களை தொடர்பு கொள்ளுங்கள்",
        items: [
          {
            subtitle: "நம்பகர்: களிமுத்து",
            body: "இந்த இணையதளம் தொடர்பான எந்த தனியுரிமை கேள்விகள், கோரிக்கைகள் அல்லது கவலைகளுக்கும், நேரடியாக கோவில் நிர்வாகத்தை தொடர்பு கொள்ளுங்கள்.",
          },
        ],
      },
    ],
    contact: {
      label: "கோவில் தொடர்பு",
      address: "கடவூர் தாலுகா, கணியாலம்பட்டி ரோடு, சுக்கம்பட்டி, கரூர், தமிழ்நாடு",
      phones: ["9597255401", "9361986095"],
      hours: "வாரம் 7 நாட்களும் திறந்திருக்கும் · காலை 4 AM – 12 PM · மாலை 4 PM – 7 PM",
    },
    disclaimer: {
      title: "மறுப்பு",
      body: "இந்த இணையதளம் பக்தி மற்றும் தகவல் நோக்கங்களுக்காக மட்டுமே பராமரிக்கப்படுகிறது. கோவில் நேரங்கள், பூஜை அட்டவணைகள், விழாக்கள் மற்றும் தெய்வ தகவல்கள் தொடர்பான அனைத்து உள்ளடக்கமும் நல்ல நம்பிக்கையில் வழங்கப்படுகிறது. மிக தற்போதைய தகவலுக்கு, நேரடியாக கோவிலை அழைக்கவும்.",
    },
  },
};

export default function PrivacyPolicyPage() {
  const { language } = useLanguage();
  const t = content[language as "en" | "ta"] ?? content.en;

  return (
    <main className="bg-neutral-950 text-neutral-100 min-h-screen font-sans selection:bg-amber-900 selection:text-amber-100">

      {/* ── HERO ── */}
      <section className="relative py-28 flex flex-col items-center text-center px-4 overflow-hidden border-b border-neutral-900">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/20 via-neutral-950 to-neutral-950 z-0" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-600/6 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-10 space-y-5 max-w-2xl mx-auto mt-10">
          <Badge
            variant="outline"
            className="text-amber-400 border-amber-500/30 bg-amber-950/30 backdrop-blur-md px-5 py-1.5 text-xs font-medium tracking-widest uppercase"
          >
            {t.badge}
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold font-serif leading-tight">
            <span className="bg-gradient-to-r from-amber-200 via-yellow-400 to-orange-400 bg-clip-text text-transparent">
              {t.title}
            </span>
          </h1>
          <p className="text-neutral-400 text-base leading-relaxed max-w-xl mx-auto">
            {t.subtitle}
          </p>
          <div className="inline-flex items-center gap-2 bg-neutral-900/60 border border-neutral-800 rounded-full px-5 py-2 text-xs text-neutral-500 backdrop-blur-sm">
            🗓️ {t.lastUpdated}
          </div>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="py-16 border-b border-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <Card className="bg-gradient-to-br from-amber-950/20 to-neutral-900 border-amber-800/30">
            <CardContent className="p-8 md:p-10 flex gap-6 items-start">
              <div className="text-4xl shrink-0">🛡️</div>
              <div className="space-y-3">
                <h2 className="text-xl font-serif font-bold text-amber-400">
                  {t.intro.title}
                </h2>
                <p className="text-neutral-400 leading-relaxed text-sm md:text-base">
                  {t.intro.body}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── POLICY SECTIONS ── */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl space-y-6">
          {t.sections.map((section, si) => (
            <Card
              key={si}
              className="bg-neutral-900/50 border-neutral-800 hover:border-neutral-700 transition-colors"
            >
              <CardContent className="p-0">
                {/* Section header */}
                <div className="flex items-center gap-4 px-8 py-6 border-b border-neutral-800">
                  <div className="w-12 h-12 rounded-2xl bg-amber-950/40 border border-amber-900/30 flex items-center justify-center text-2xl shrink-0">
                    {section.icon}
                  </div>
                  <h2 className="text-lg md:text-xl font-serif font-bold text-amber-400">
                    {section.title}
                  </h2>
                </div>

                {/* Section items */}
                <div className="divide-y divide-neutral-800/60">
                  {section.items.map((item, ii) => (
                    <div key={ii} className="px-8 py-6 space-y-2">
                      <h3 className="text-sm font-semibold text-neutral-200 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                        {item.subtitle}
                      </h3>
                      <p className="text-neutral-500 text-sm leading-relaxed pl-3.5">
                        {item.body}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── DISCLAIMER ── */}
      <section className="py-8 border-t border-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <Card className="bg-neutral-900/40 border-neutral-800">
            <CardContent className="p-7 flex gap-5 items-start">
              <span className="text-2xl shrink-0">⚠️</span>
              <div className="space-y-2">
                <h3 className="font-serif font-bold text-neutral-300">
                  {t.disclaimer.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {t.disclaimer.body}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── CONTACT STRIP ── */}
      <section className="py-14 border-t border-neutral-900 bg-gradient-to-t from-amber-950/10 to-neutral-950">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-2">
              <p className="text-xs text-amber-500/70 uppercase tracking-widest font-medium">
                {t.contact.label}
              </p>
              <p className="text-lg font-serif font-bold text-neutral-200">
                18m Padi Karuppanasamy Temple
              </p>
              <p className="text-neutral-500 text-sm">{t.contact.address}</p>
              <Separator className="bg-neutral-800 my-3" />
              <div className="flex items-center gap-2 text-xs text-neutral-600">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                {t.contact.hours}
              </div>
            </div>

            <div className="space-y-3">
              {t.contact.phones.map((num, i) => (
                <a key={i} href={`tel:${num}`} className="block">
                  <div className="flex items-center justify-between bg-neutral-900/60 border border-neutral-800 hover:border-amber-700/40 rounded-xl px-5 py-4 group transition-colors">
                    <span className="text-amber-400 font-mono tracking-wider text-sm">
                      +91 {num}
                    </span>
                    <span className="text-neutral-600 group-hover:text-amber-500 transition-colors">
                      📞
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}